import { useRef, useState } from 'react';
import { UserRoles } from '@/enums/enums';
import { useTeachers } from '@/features/teacher/teacher.queries';
import { getUserOptions } from '@/features/user/user.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { TeacherNameColumn } from '@/components/datatable/columns/teachers/teacher-column';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import CreateUpdateTeacherModal from '@/components/modals/create-update-teacher-modal';
import DeleteTeacherModal from '@/components/modals/delete-teacher-modal';
import TeacherResetPasswordModal from '@/components/modals/teacher-reset-password-modal';
import TeacherSheet from '@/components/sheets/teacher-sheet/teacher-view-sheet';

const VISIBLE_TO_ROLES = [UserRoles.Root, UserRoles.Admin, UserRoles.Secretary];

const teacherRouteSchema = z.object({
  mode: z.enum(['create', 'update', 'view']).optional(),
  teacher: z.number().optional(),
});

export const Route = createFileRoute('/_app/(teachers)/teachers/')({
  validateSearch: zodSearchValidator(teacherRouteSchema),
  component: Teachers,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (!VISIBLE_TO_ROLES.includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function Teachers() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <SubjectsTabHeader />
      <TeachersTable />
    </div>
  );
}

function SubjectsTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['teacher', 'common']);

  const createSubject = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createSubject} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {toCapitalize(t('teacher', { ns: 'common' }))}
        </Button>
      </div>
      <CreateUpdateTeacherModal />
    </div>
  );
}

function TeachersTable() {
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const teacher = useRef({ id: 0, name: '' });
  const teacherResetPasswordData = useRef({ id: 0, name: '' });
  const { t } = useTranslation(['table', 'common']);
  const { data = [], isLoading } = useTeachers();

  const updateTeacher = (id: number) => {
    navigate({ to: '.', search: { mode: 'update', teacher: id } });
  };

  const deleteTeacher = (id: number, name: string) => {
    teacher.current = { id, name };
    setIsDeleteModalOpen(true);
  };

  const resetTeacherPassword = (id: number, name: string) => {
    teacherResetPasswordData.current = { id, name };
    setIsResetPasswordModalOpen(true);
  };

  return (
    <>
      <DataTableSimple
        data={data}
        columns={['id', 'username', 'name', 'phone', 'email', 'birthDate', 'information']}
        isLoading={isLoading}
        columnsLabels={{
          id: t('serial_id'),
          username: t('username'),
          name: t('name'),
          phone: t('phone'),
          email: 'Email',
          birthDate: t('birth_date'),
          information: t('information'),
        }}
        customRender={{
          name: (row) => (
            <TeacherNameColumn teacherName={row.getValue('name')} id={row.getValue('id')} />
          ),
        }}
        extraColumns={[
          {
            id: 'actions',
            size: 50,
            cell: ({ row }) => (
              <DataTableRowActions
                row={row}
                menuItems={[
                  {
                    label: t('edit', { ns: 'common' }),
                    onClick: () => updateTeacher(row.getValue('id')),
                    isLoading: false,
                  },
                  {
                    label: t('reset_password', { ns: 'common' }),
                    onClick: () => resetTeacherPassword(row.getValue('id'), row.getValue('name')),
                    isLoading: false,
                  },
                  {
                    label: t('delete', { ns: 'common' }),
                    onClick: () => deleteTeacher(row.getValue('id'), row.getValue('name')),
                    isLoading: false,
                  },
                ]}
              />
            ),
          },
        ]}
        className='flex h-full flex-col overflow-y-auto'
      />
      <TeacherSheet />
      <DeleteTeacherModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        teacher={teacher.current}
      />
      <TeacherResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        teacherId={teacherResetPasswordData.current.id}
        teacherName={teacherResetPasswordData.current.name}
      />
    </>
  );
}
